import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Input, Modal, Select, Spinner, useToast } from "@aigntx/ui";
import { endpoints } from "../api";
import { FormFieldGrid, missingRequired } from "../components/FormFields";
import type { FormValues } from "../components/FormFields";
import type { FormsMeta, FormType, ProjectDetail, ProjectForm } from "../types";

export function statusTone(f: ProjectForm): "good" | "warn" | "danger" | "accent" | "neutral" {
  if (f.status === "closed") return f.outcomeTone === "bad" ? "danger" : "good";
  if (f.overdue) return "danger";
  if (f.outcomeTone === "warn") return "warn";
  return "accent";
}

/**
 * The order the families are worked in, not alphabetical order. A supervision
 * consultant's day starts with what the Contractor has submitted and ends with
 * what it costs, so the page reads top to bottom the way the job does.
 */
const FAMILIES = [
  "submittals",
  "correspondence",
  "quality",
  "reports",
  "hse",
  "variations",
  "payments",
] as const;

type Tab = "register" | "types";

/**
 * Every form on this project, and every form that COULD be on it.
 *
 * The two were previously the same dropdown wearing two hats — one listing all
 * fifteen types as a filter, one listing the handful you may raise — which read
 * as two different systems. They are now two named tabs: what has happened, and
 * what can happen.
 */
export function ProjectFormsPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [catalog, setCatalog] = useState<FormType[]>([]);
  const [forms, setForms] = useState<ProjectForm[] | null>(null);
  const [filter, setFilter] = useState(params.get("code") ?? "");
  const [family, setFamily] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [raising, setRaising] = useState<FormType | null>(null);
  const [meta, setMeta] = useState<FormsMeta | null>(null);
  const [error, setError] = useState("");

  const tab: Tab = params.get("tab") === "types" ? "types" : "register";
  const setTab = (next: Tab) => {
    const q = new URLSearchParams(params);
    if (next === "register") q.delete("tab");
    else q.set("tab", next);
    setParams(q, { replace: true });
  };

  useEffect(() => {
    Promise.all([
      endpoints.getProject(id),
      endpoints.formsCatalog(),
      endpoints.listForms(id),
      endpoints.formsMeta(id),
    ])
      .then(([p, c, f, m]) => {
        setProject(p);
        setCatalog(c);
        setForms(f);
        setMeta(m);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  const byCode = useMemo(() => new Map(catalog.map((c) => [c.code, c])), [catalog]);

  /** How many of each type are actually on this project — the number that makes
   * an empty filter obviously empty instead of mysteriously empty. */
  const counts = useMemo(() => {
    const out = new Map<string, number>();
    for (const f of forms ?? []) out.set(f.code, (out.get(f.code) ?? 0) + 1);
    return out;
  }, [forms]);

  const shown = useMemo(() => {
    let list = forms ?? [];
    if (filter) list = list.filter((f) => f.code === filter);
    if (family) list = list.filter((f) => byCode.get(f.code)?.family === family);
    if (openOnly) list = list.filter((f) => f.status === "open");
    if (mineOnly) list = list.filter((f) => f.canAct);
    return list;
  }, [forms, filter, family, openOnly, mineOnly, byCode]);

  const mine = (forms ?? []).filter((f) => f.canAct).length;

  // Only the forms this user's party is allowed to originate — a Contractor
  // never sees "raise a Variation Order".
  const canRaise = (code: string) => Boolean(meta?.canRaise.includes(code));
  const raisable = useMemo(() => catalog.filter((c) => canRaise(c.code)), [catalog, meta]);

  /** Who raises this type, in words, for the types you cannot start yourself. */
  const raisedBy = (spec: FormType) =>
    spec.originator
      .map((role) => t(`party.${role}`, { defaultValue: role }))
      .filter((v, i, all) => all.indexOf(v) === i)
      .join(" / ");

  function openRegisterFor(code: string) {
    setFilter(code);
    setFamily("");
    setMineOnly(false);
    setOpenOnly(false);
    setTab("register");
  }

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!forms || !project || !meta)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  return (
    <div className="page">
      <div className="crumb">
        <Link to="/projects">{t("projects.title")}</Link>{" "}
        <span className="muted">/ </span>
        <Link to={`/projects/${id}`}>{project.name}</Link>{" "}
        <span className="muted">/ {t("forms.title")}</span>
      </div>

      <header className="page__head between">
        <div>
          <h1 className="page__title">{t("forms.title")}</h1>
          <p className="page__sub">{t("forms.registerHint")}</p>
        </div>
        <div className="forms-you">
          <Badge tone="neutral">{t("forms.youAre", { party: meta.partyLabel })}</Badge>
          {mine > 0 && (
            <button type="button" className="chip is-on" onClick={() => { setMineOnly(true); setTab("register"); }}>
              {t("forms.withYou", { count: mine })}
            </button>
          )}
        </div>
      </header>

      <div className="tabs">
        <button
          type="button"
          className={tab === "register" ? "tab is-on" : "tab"}
          onClick={() => setTab("register")}
        >
          {t("forms.tabRegister", { count: forms.length })}
        </button>
        <button
          type="button"
          className={tab === "types" ? "tab is-on" : "tab"}
          onClick={() => setTab("types")}
        >
          {t("forms.tabTypes", { count: catalog.length })}
        </button>
      </div>

      {tab === "register" ? (
        <Card>
          <div className="tab-toolbar">
            <div className="forms-filters">
              <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="">{t("forms.allTypes")}</option>
                {catalog.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.title} ({counts.get(c.code) ?? 0})
                  </option>
                ))}
              </Select>
              <button
                type="button"
                className={openOnly ? "chip is-on" : "chip"}
                onClick={() => setOpenOnly((v) => !v)}
              >
                {t("forms.openOnly")}
              </button>
              <button
                type="button"
                className={mineOnly ? "chip is-on" : "chip"}
                onClick={() => setMineOnly((v) => !v)}
              >
                {t("forms.mineOnly")}
              </button>
            </div>
            {raisable.length > 0 && (
              <div className="forms-raise">
                <Select
                  value=""
                  onChange={(e) => {
                    const spec = raisable.find((c) => c.code === e.target.value);
                    if (spec) setRaising(spec);
                  }}
                >
                  <option value="">{t("forms.raise")}</option>
                  {FAMILIES.filter((fam) => raisable.some((c) => c.family === fam)).map((fam) => (
                    <optgroup key={fam} label={t(`family.${fam}`)}>
                      {raisable
                        .filter((c) => c.family === fam)
                        .map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.code} — {c.title}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </Select>
              </div>
            )}
          </div>

          <div className="chips forms-fams">
            <button
              type="button"
              className={family === "" ? "chip is-on" : "chip"}
              onClick={() => setFamily("")}
            >
              {t("forms.allFamilies")}
            </button>
            {FAMILIES.map((fam) => (
              <button
                key={fam}
                type="button"
                className={family === fam ? "chip is-on" : "chip"}
                onClick={() => setFamily((v) => (v === fam ? "" : fam))}
              >
                {t(`family.${fam}`)}
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <p className="muted">
              {forms.length === 0 ? t("forms.empty") : t("forms.emptyFiltered")}
            </p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t("forms.number")}</th>
                    <th>{t("forms.subject")}</th>
                    <th>{t("forms.raisedBy")}</th>
                    <th>{t("forms.state")}</th>
                    <th>{t("forms.due")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((f) => (
                    <tr key={f.id} className={f.canAct ? "is-mine" : undefined}>
                      <td>
                        <Link className="mono form-no" to={`/projects/${id}/forms/${f.id}`}>
                          {f.number}
                        </Link>
                        {f.rev > 0 && <span className="muted"> rev {f.rev}</span>}
                        <div className="u-mail">{f.title}</div>
                      </td>
                      <td>
                        {f.subject}
                        {f.parentNumber && (
                          <div className="u-mail mono">← {f.parentNumber}</div>
                        )}
                      </td>
                      <td>
                        <div className="u-name">{f.originatorName}</div>
                        <div className="u-mail">{f.originatorGroupName || f.originatorParty}</div>
                      </td>
                      <td>
                        <Badge tone={statusTone(f)}>
                          {f.status === "closed" ? f.outcomeLabel || t("forms.closed") : f.stepLabel}
                        </Badge>
                        {f.status === "open" && (
                          <div className="u-mail">
                            {f.canAct
                              ? t("forms.actionYou")
                              : t("forms.waitingOn", { party: f.pendingPartyLabel })}
                          </div>
                        )}
                      </td>
                      <td className="mono">
                        {f.dueAt ? new Date(f.dueAt).toLocaleDateString() : "—"}
                        {f.overdue && <Badge tone="danger">{t("forms.overdue")}</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : (
        <>
          <Card className="ftypes__intro">
            <p className="page__sub">{t("forms.typesHint", { party: meta.partyLabel })}</p>
          </Card>
          {FAMILIES.map((fam) => {
            const group = catalog.filter((c) => c.family === fam);
            if (group.length === 0) return null;
            return (
              <section key={fam} className="ftypes__fam">
                <h2 className="ftypes__famname">{t(`family.${fam}`)}</h2>
                <div className="ftypes">
                  {group.map((spec) => {
                    const count = counts.get(spec.code) ?? 0;
                    const yours = canRaise(spec.code);
                    return (
                      <Card key={spec.code} className={yours ? "ftype is-yours" : "ftype"}>
                        <div className="ftype__head">
                          <span className="mono ftype__code">{spec.code}</span>
                          <span className="ftype__title">{spec.title}</span>
                        </div>
                        <p className="ftype__summary">{spec.summary}</p>
                        <div className="ftype__route">
                          <span className="muted">{t("forms.goesTo")}</span>{" "}
                          {spec.route.map((s, i) => (
                            <span key={s.key}>
                              {i > 0 && <span className="muted"> → </span>}
                              <Badge tone={i === 0 ? "accent" : "neutral"}>{s.actorLabel}</Badge>
                            </span>
                          ))}
                        </div>
                        <dl className="ftype__facts">
                          <div>
                            <dt>{t("forms.factRaisedBy")}</dt>
                            <dd>{raisedBy(spec)}</dd>
                          </div>
                          <div>
                            <dt>{t("forms.factReplyBy")}</dt>
                            <dd>
                              {spec.slaDays
                                ? t("forms.factDays", { count: spec.slaDays })
                                : t("forms.factNoSla")}
                            </dd>
                          </div>
                          <div>
                            <dt>{t("forms.factFiledIn")}</dt>
                            <dd>{spec.folder}</dd>
                          </div>
                        </dl>
                        <div className="ftype__foot">
                          <button
                            type="button"
                            className="ftype__count"
                            onClick={() => openRegisterFor(spec.code)}
                          >
                            {count === 0
                              ? t("forms.noneYet")
                              : t("forms.onThisProject", { count })}
                          </button>
                          {yours ? (
                            <Button onClick={() => setRaising(spec)}>{t("forms.raiseThis")}</Button>
                          ) : (
                            <span className="muted ftype__notyours">
                              {t("forms.notYours", { party: raisedBy(spec) })}
                            </span>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </>
      )}

      {raising && (
        <RaiseFormModal
          projectId={id}
          spec={raising}
          onClose={() => setRaising(null)}
          onRaised={(f) => {
            setRaising(null);
            setForms((list) => [f, ...(list ?? [])]);
            setTab("register");
            toast.success(t("forms.raised", { number: f.number }));
          }}
        />
      )}
    </div>
  );
}

/**
 * Also used by the supervision desk and by a form raising its own follow-on.
 *
 * `parentFormId` is what makes a chain a chain: raised from a parent, the new
 * form carries the link, and both registers show which one led to the other.
 */
export function RaiseFormModal({
  projectId,
  spec,
  parentFormId,
  initialSubject = "",
  onClose,
  onRaised,
}: {
  projectId: string;
  spec: FormType;
  parentFormId?: string;
  initialSubject?: string;
  onClose: () => void;
  onRaised: (f: ProjectForm) => void;
}) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState(initialSubject);
  const [values, setValues] = useState<FormValues>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    const missing = missingRequired(spec.fields, values);
    if (missing.length) {
      setError(t("forms.required", { fields: missing.join(", ") }));
      return;
    }
    setBusy(true);
    setError("");
    try {
      onRaised(
        await endpoints.raiseForm(projectId, {
          code: spec.code,
          subject,
          data: values,
          parentFormId,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const firstStep = spec.route[0];

  return (
    <Modal
      open
      title={`${spec.code} — ${spec.title}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("forms.submit")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      <p className="muted form-summary">{spec.summary}</p>
      <div className="form-route">
        <span className="muted">{t("forms.goesTo")}</span>{" "}
        {spec.route.map((s, i) => (
          <span key={s.key}>
            {i > 0 && <span className="muted"> → </span>}
            <Badge tone={i === 0 ? "accent" : "neutral"}>{s.actorLabel}</Badge>
          </span>
        ))}
      </div>
      <Field label={t("forms.subject")}>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={spec.title} />
      </Field>
      <FormFieldGrid
        fields={spec.fields}
        values={values}
        onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
      />
      {firstStep && (
        <p className="muted">{t("forms.nextWith", { party: firstStep.actorLabel })}</p>
      )}
    </Modal>
  );
}
